import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { formatCurrency, formatCurrencyAdaptive, formatNumber } from '@/lib/utils';

import { useOperador } from '../configuracion.api';
import { fechaCorta, mesActual, periodoDeMes } from '../lib/configuracion-format';
import type { Operador } from '../configuracion.types';

interface OperadorDetalleDialogoProps {
  operador: Operador;
  onClose: () => void;
}

/** Indicador del detalle (ventas y montos del período). */
function Indicador({ titulo, valor, detalle }: { titulo: string; valor: string; detalle?: string }) {
  return (
    <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">{titulo}</p>
      <p className="mt-0.5 text-base font-bold tabular-nums text-ink-900">{valor}</p>
      {detalle ? <p className="text-[11px] text-ink-500">{detalle}</p> : null}
    </div>
  );
}

/**
 * Detalle de un operador con sus ventas y comisiones del mes seleccionado.
 *
 * Puerto de `visa_operators/view`: el panel anterior fijaba el mes en curso; aquí
 * se puede elegir el mes, porque las ventas por operador son esporádicas y con el
 * mes actual la pantalla se vería siempre en cero.
 */
export function OperadorDetalleDialogo({ operador, onClose }: OperadorDetalleDialogoProps) {
  const [mes, setMes] = useState(() => mesActual());

  const periodo = periodoDeMes(mes);
  const detalle = useOperador(operador.id, periodo);

  const ventas = detalle.data?.ventas;
  const comision = detalle.data?.comision;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Operador ${operador.nombre}`}
      description={`Código ${operador.codigo} · comisión ${formatNumber(operador.comision)} %`}
      size="lg"
      footer={
        <label className="mr-auto flex items-center gap-2 text-xs font-medium text-ink-500">
          Mes
          <CampoFecha
           
            value={mes}
            onChange={(event) => setMes(event.target.value)}
            className="h-9 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
           tipo="month" />
        </label>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={operador.activo ? 'success' : 'neutral'}>
            {operador.activo ? 'Activo' : 'Inactivo'}
          </Badge>
          <span className="text-xs text-ink-500">
            Registrado el {fechaCorta(operador.fecha_creacion)}
            {operador.creado_por_nombre ? ` por ${operador.creado_por_nombre}` : ''}
          </span>
        </div>

        {operador.descripcion ? (
          <p className="rounded-control bg-ink-50 px-3 py-2 text-sm text-ink-600">
            {operador.descripcion}
          </p>
        ) : null}

        <div className="rounded-control border border-ink-100 px-3 py-2 text-xs text-ink-500">
          Período consultado: {fechaCorta(periodo.fecha_inicio)} al {fechaCorta(periodo.fecha_fin)}
        </div>

        {detalle.isPending ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-20 rounded-control" />
            <Skeleton className="h-20 rounded-control" />
            <Skeleton className="h-20 rounded-control" />
            <Skeleton className="h-20 rounded-control" />
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Indicador
                titulo="Total de ventas"
                valor={formatNumber(ventas?.total_ventas ?? 0)}
                detalle="Ventas con este operador"
              />
              <Indicador
                titulo="Monto total"
                valor={formatCurrencyAdaptive(ventas?.monto_total ?? 0, 100_000)}
                detalle="Cobrado con este operador"
              />
              <Indicador
                titulo="Verificados"
                valor={formatNumber(ventas?.verificadas ?? 0)}
                detalle="Pagos ya verificados"
              />
              <Indicador
                titulo="Pendientes"
                valor={formatNumber(ventas?.pendientes ?? 0)}
                detalle="Pagos por verificar"
              />
            </div>

            {(ventas?.total_ventas ?? 0) === 0 ? (
              <p className="rounded-control border border-dashed border-ink-200 px-3 py-6 text-center text-sm text-ink-500">
                No hay ventas con este operador en el mes seleccionado.
              </p>
            ) : (
              <div className="rounded-control border border-ink-100">
                <p className="border-b border-ink-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
                  Comisiones estimadas
                </p>

                <dl className="divide-y divide-ink-50 text-sm">
                  <div className="flex items-center justify-between px-3 py-2">
                    <dt className="text-ink-600">Monto bruto</dt>
                    <dd className="tabular-nums text-ink-900">
                      {formatCurrency(comision?.monto_bruto ?? 0)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <dt className="text-ink-600">
                      Comisión ({formatNumber(comision?.tasa ?? 0)} %)
                    </dt>
                    <dd className="tabular-nums text-amber-700">
                      − {formatCurrency(comision?.comision_estimada ?? 0)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between bg-ink-50/60 px-3 py-2">
                    <dt className="font-semibold text-ink-700">Monto neto</dt>
                    <dd className="font-bold tabular-nums text-ink-900">
                      {formatCurrency(comision?.monto_neto ?? 0)}
                    </dd>
                  </div>
                </dl>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
